import React, { useState, useRef } from 'react';
import { CertificatePreviewProps, TestReportHandle } from '../../../types/certificate';
import { useCertificatePreview } from '../hooks/useCertificatePreview';
import { saveCertificateWithReport } from '../../../services/fileService';
import { TestReport } from '../../test-report/components/TestReport';
import styles from './CertificatePreview.module.css';

/**
 * 产品合格证预览组件
 * 严格按照模板1:1还原设计
 * 具备PDF生成、下载和二维码功能
 */
export const CertificatePreview: React.FC<CertificatePreviewProps> = ({
  certificateData,
  onDownloadPDF,
  className = ''
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const testReportRef = useRef<TestReportHandle>(null);

  const { 
    generatePDF, 
    downloadPDF, 
    isGenerating, 
    qrCodeDataUrl,
    certificateRef,
    reportId,
    reportUrl
  } = useCertificatePreview({
    certificateId: certificateData.orderNumber
  });

  // 构建检测报告数据
  const getTestReportData = () => ({
    orderNumber: certificateData.orderNumber,
    quantity: certificateData.bucketCount,
    batchNumber: certificateData.batchNumber,
    productionDate: certificateData.productionDate,
    generationDate: certificateData.testReportDate
  });

  const handleDownload = async () => {
    try {
      await downloadPDF(`certificate-${certificateData.orderNumber}.pdf`);
      onDownloadPDF?.();
    } catch (error) {
      console.error('下载失败:', error);
    }
  };

  const handleConfirmSave = async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    
    try {
      console.log('开始保存合格证和检测报告...');
      
      // 生成合格证PDF
      const certificatePdf = await generatePDF();
      const certificateBase64 = certificatePdf.output('datauri').split(',')[1];
      console.log('✅ 合格证PDF生成完成，Base64长度:', certificateBase64.length);
      
      // 生成检测报告PDF
      if (!testReportRef.current) {
        throw new Error('检测报告组件未加载');
      }
      
      const reportPdf = await testReportRef.current.generatePDF();
      const reportBase64 = reportPdf.output('datauri').split(',')[1];
      console.log('✅ 检测报告PDF生成完成，Base64长度:', reportBase64.length);
      
      // 验证PDF数据有效性
      if (!certificateBase64 || certificateBase64.length < 1000) {
        throw new Error('合格证PDF生成失败或数据太小');
      }
      
      if (!reportBase64 || reportBase64.length < 1000) {
        throw new Error('检测报告PDF生成失败或数据太小');
      }
      
      // 准备保存数据，传递reportId
      const certificateRequestData = {
        ...certificateData,
        pdfBase64: certificateBase64,
        reportId: reportId  // 确保reportId被传递
      };
      
      const reportData = {
        ...getTestReportData(),
        pdfBase64: reportBase64,
        reportId: reportId
      };
      
      console.log('📤 开始上传PDF数据到服务器...');
      
      // 调用保存服务
      const result = await saveCertificateWithReport(certificateRequestData, reportData, reportId);
      
      console.log('✅ 保存成功:', result);
      setIsSaved(true);
      alert(`保存成功！\n合格证文件: ${result.files.certificate.size} bytes\n检测报告文件: ${result.files.report.size} bytes`);
      
    } catch (error) {
      console.error('❌ 保存失败:', error);
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      alert(`保存失败: ${errorMessage}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`${styles.certificatePreview} ${className}`}>
      {/* 隐藏的检测报告组件，用于PDF生成 */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        <TestReport reportData={getTestReportData()} ref={testReportRef} />
      </div>

      <div ref={certificateRef} className={styles.certificate}>
        {/* 页眉 - Logo和标题 */}
        <div className={styles.header}>
          <div style={{ width: "120px" }}>{/* 左侧空白平衡布局 */}</div>

          <div className={styles.title}>
            <h1 className={styles.titleEn}>Certificate of Conformity</h1>
            <h2 className={styles.titleCn}>产品合格证</h2>
          </div>

          <div>
            {/* Jotun Logo - 移到右上角 */}
            <img src="/logo.png" alt="Jotun Logo" className={styles.logo} />
          </div>
        </div>

        {/* 描述文本 */}
        <div className={styles.description}>
          <p className={styles.descriptionEn}>
            We hereby certify that the following products have been strictly
            tested according to quality
            <br />
            control standards and comply with product specifications of Jotun.
          </p>
          <p className={styles.descriptionCn}>
            兹证明，以下订单产品已按照佐敦质量控制标准进行了严格测试，结果合格，准予出厂。
          </p>
        </div>

        {/* 订单信息行 */}
        <div className={styles.orderInfo}>
          <div className={styles.orderNumber}>
            <span>订单号</span>
            <span className={styles.spanOrderNo}>/Order No:</span>
            <strong>{certificateData.orderNumber}</strong>
          </div>
          <div className={styles.shipmentDate}>
            <span>发货日期</span>
            <span className={styles.spanOrderNo}>/Shipment Date:</span>
            <strong>{certificateData.shipmentDate}</strong>
          </div>
        </div>

        {/* 产品表格 */}
        <table className={styles.productTable}>
          <thead style={{fontSize: '14px'}}>
            <tr>
              <th className={styles.productNameHeader}>
                <span className="english-text">Product Name</span>
                <br />
                产品名称
              </th>
              <th className={styles.batchNoHeader}>
                <span className="english-text">Batch No</span>
                <br />
                批号
              </th>
              <th className={styles.qtyHeader}>
                <span className="english-text">Qty</span>
                <br />
                桶数
              </th>
              <th className={styles.prodDateHeader}>
                <span className="english-text">Prod. Date</span>
                <br />
                生产日期
              </th>
              <th className={styles.shelfLifeHeader}>
                <span className="english-text">Shelf life</span>
                <br />
                保质期(<span className="english-text">D</span>)
              </th>
            </tr>
          </thead>
          <tbody>
            {/* 第一行显示数据 */}
            <tr className={styles.dataRow}>
              <td className={`${styles.productNameCell} chinese-content`} style={{fontSize: '10px'}}>
                <span className="chinese-text">
                  GT-NSP-Fp2.00-Steelmaster 60WB 室内膨胀型钢结构防火涂料 白色
                </span>
                <br />
                <span className="english-part">
                  STEELMASTER 60WB WHITE 18.5L
                </span>
              </td>
              <td className="number-text">{certificateData.batchNumber}</td>
              <td className="number-text">{certificateData.bucketCount}</td>
              <td className="number-text">{certificateData.productionDate}</td>
              <td className="number-text">183</td>
            </tr>

            {/* 空行 - 增加到19行 */}
            {[...Array(17)].map((_, index) => (
              <tr key={index} className={styles.emptyRow}>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 页脚 */}
        <div className={styles.footer}>
          {/* 文字部分 */}
          <div className={styles.qrText}>
            <p className={styles.qrTextEn}>
              If needed, please scan the following QR code for the Certificate
              of Analysis (COA).
            </p>
            <p className={styles.qrTextCn}>
              如有需要，可扫描随附二维码以获取产品检测报告(COA).
            </p>
          </div>

          <div className={styles.qrSection}>
            {/* 二维码区域 */}
            <div className={styles.qrCode}>
              {qrCodeDataUrl ? (
                <img
                  src={qrCodeDataUrl}
                  alt="QR Code for Test Report"
                  className={styles.qrImage}
                />
              ) : (
                <div className={styles.qrPlaceholder}>二维码</div>
              )}
            </div>
            {/* 右侧区域 - 质量控制实验室和公章 */}
            <div className={styles.stampSection}>
              {/* 质量控制实验室文本 */}
              <div className={styles.qualityLab}>
                <p className={styles.qualityLabEn}>
                  Quality Control Laboratory
                </p>
                <p className={styles.qualityLabEn}>
                  Jotun Coatings (Zhangjiagang) Co., Ltd.
                </p>
                <p className={styles.qualityLabCn}>
                  佐敦涂料（张家港）有限公司品控实验室
                </p>
              </div>

              {/* 公章 */}
              <div className={styles.stamp}>
                <img
                  src="/seal.png"
                  alt="公司公章"
                  className={styles.stampImage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 操作按钮 */}
      <div className={styles.actions}>
        {!isSaved ? (
          <button
            className={styles.confirmButton}
            onClick={handleConfirmSave}
            disabled={isSaving || isGenerating}
          >
            {isSaving ? "确认保存中..." : "✅ 确认合格证和检测报告"}
          </button>
        ) : (
          <div className={styles.savedStatus}>
            <span className={styles.savedText}>✅ 已确认并保存</span>
          </div>
        )}

        <button
          className={styles.downloadButton}
          onClick={handleDownload}
          disabled={isGenerating || isSaving}
        >
          {isGenerating ? "生成中..." : "下载合格证PDF"}
        </button>
      </div>
    </div>
  );
};